import { AutoColumn } from 'components/Column'
import PoweredByKodiak from 'components/PoweredByKodiak'
import Row from 'components/Row'
import { SearchIcon, SearchInput } from 'components/SearchModal/styleds'
import { SwitchLocaleLink } from 'components/SwitchLocaleLink'
import { isSupportedChain, SupportedChainId } from 'config/chains'
import { useConnection } from 'hooks/useConnection'
import useParsedQueryString from 'hooks/useParsedQueryString'
import { getParsedChainId } from 'lib/utils/utils'
import { useCallback, useState } from 'react'
import { RefreshCw } from 'react-feather'
import { Navigate, useLocation } from 'react-router-dom'
import { useToggleWalletModal } from 'state/application/hooks'
import { useSearchTerm as useFarmSearchTerm, useSetFarmSearchTerm } from 'state/kodiakFarms/hooks'
import {
  useSearchTerm as useIslandSearchTerm,
  useSetSearchTerm as useSetIslandSearchTerm,
} from 'state/kodiakIslands/hooks'
import { useTheme } from 'styled-components'
import { ChainAwareLink, ThemedText } from 'theme'

import * as S from './Liquidity.styles'
import { RefreshButton } from './Liquidity.styles'
import FarmHeader from './ProtocolHeaders/FarmHeader'
import IslandHeader from './ProtocolHeaders/IslandHeader'
import V2Header from './ProtocolHeaders/V2Header'
import V3Header from './ProtocolHeaders/V3Header'
import Farms from './ProtocolLists/farms'
import Islands from './ProtocolLists/islands'
import PoolV2 from './ProtocolLists/v2'
import PoolV3 from './ProtocolLists/v3'
function WrongNetworkCard() {
  const theme = useTheme()
  return (
    <>
      <S.PageWrapper style={{ padding: 0 }}>
        <AutoColumn gap="lg" style={{ width: '100%' }}>
          <S.MainContentWrapper>
            <S.ErrorContainer>
              <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                <S.NetworkIcon strokeWidth={1.2} />
                <div data-testid="pools-unsupported-err">
                  <>Your connected network is unsupported.</>
                </div>
              </ThemedText.DeprecatedBody>
            </S.ErrorContainer>
          </S.MainContentWrapper>
        </AutoColumn>
      </S.PageWrapper>
    </>
  )
}

export interface RefreshControl {
  onRegister: (callback: (() => Promise<void>) | null) => void
  isRefreshing: boolean
}

type LiquidityTab = 'pools' | 'v3' | 'v2' | 'farms'

const TAB_LABELS: Record<LiquidityTab, string> = {
  pools: 'Top Pools',
  v3: 'Custom V3',
  v2: 'Custom V2',
  farms: 'Farms',
}

const DEFAULT_TABS: readonly LiquidityTab[] = ['pools', 'v3', 'v2']

const TABS_BY_CHAIN: Partial<Record<SupportedChainId, readonly LiquidityTab[]>> = {
  [SupportedChainId.BERA]: ['pools', 'v3', 'v2', 'farms'],
  [SupportedChainId.ROBINHOOD]: ['v3', 'v2'],
}

const getCurrentTab = (pathname: string): LiquidityTab => {
  if (pathname.includes('/liquidity/v3')) return 'v3'
  if (pathname.includes('/liquidity/v2')) return 'v2'
  if (pathname.includes('/liquidity/farms')) return 'farms'
  return 'pools'
}

const Liquidity = () => {
  const location = useLocation()
  const { account, chainId } = useConnection()
  const toggleWalletModal = useToggleWalletModal()

  const urlChainId = getParsedChainId(useParsedQueryString())
  const tabs = TABS_BY_CHAIN[urlChainId ?? chainId] ?? DEFAULT_TABS
  const currentTab = getCurrentTab(location.pathname)

  const islandSearchTerm = useIslandSearchTerm()
  const setIslandSearchTerm = useSetIslandSearchTerm()
  const farmSearchTerm = useFarmSearchTerm()
  const setFarmSearchTerm = useSetFarmSearchTerm()

  const isFarmsTab = location.pathname.includes('/liquidity/farms')
  const searchTerm = isFarmsTab ? farmSearchTerm : islandSearchTerm
  const setSearchTerm = isFarmsTab ? setFarmSearchTerm : setIslandSearchTerm

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshCallback, setRefreshCallback] = useState<(() => Promise<void>) | null>(null)

  const handleSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleRefresh = useCallback(async () => {
    if (!refreshCallback || isRefreshing) return

    setIsRefreshing(true)
    try {
      await refreshCallback()
    } catch (error) {
      console.error('Refresh failed:', error)
    } finally {
      setTimeout(() => setIsRefreshing(false), 500)
    }
  }, [refreshCallback, isRefreshing])

  const registerRefresh = useCallback((callback: (() => Promise<void>) | null) => {
    setRefreshCallback(() => callback)
  }, [])

  const renderHeader = () => {
    if (location.pathname.includes('/v3')) return <V3Header />
    if (location.pathname.includes('/v2')) return <V2Header />
    if (location.pathname.includes('farms')) return <FarmHeader />
    return <IslandHeader />
  }

  const renderBody = () => {
    const refreshControl = {
      onRegister: registerRefresh,
      isRefreshing,
    }

    if (location.pathname.includes('/liquidity/v3')) {
      return (
        <PoolV3
          account={account}
          chainId={chainId}
          toggleWalletModal={toggleWalletModal}
          searchParam={searchTerm}
          refreshControl={refreshControl}
        />
      )
    } else if (location.pathname.includes('/liquidity/v2')) {
      return (
        <PoolV2
          account={account}
          toggleWalletModal={toggleWalletModal}
          searchParam={searchTerm}
          refreshControl={refreshControl}
        />
      )
    } else if (location.pathname.includes('/liquidity/farms')) {
      return <Farms refreshControl={refreshControl} />
    } else {
      return <Islands refreshControl={refreshControl} />
    }
  }

  const renderNavbar = () => {
    return (
      <S.FilterFlex>
        <S.Switch>
          {tabs.map((tab) => (
            <ChainAwareLink key={tab} to={`/liquidity/${tab}`}>
              <S.Option active={location.pathname.includes(`/liquidity/${tab}`)}>
                <ThemedText.DeprecatedBody
                  fontSize="12px !important"
                  margin="0px 16px"
                  lineHeight="20px"
                  fontWeight="500"
                >
                  {TAB_LABELS[tab]}
                </ThemedText.DeprecatedBody>
              </S.Option>
            </ChainAwareLink>
          ))}
        </S.Switch>
        <>
          <S.SearchWrapper>
            <Row gap="6px">
              <SearchIcon height={20} width={20} />
              <SearchInput placeholder="Enter token symbol" onChange={handleSearch} value={searchTerm} />
              <RefreshButton
                onClick={handleRefresh}
                disabled={!refreshCallback || isRefreshing}
                isRefreshing={isRefreshing}
                title="Refresh data"
              >
                <RefreshCw size={16} />
              </RefreshButton>
            </Row>
          </S.SearchWrapper>
        </>
      </S.FilterFlex>
    )
  }

  if (!tabs.includes(currentTab)) {
    return <Navigate to={{ ...location, pathname: `/liquidity/${tabs[0]}` }} replace />
  }

  return (
    <S.PageWrapper>
      {renderHeader()}
      {renderNavbar()}
      {account && !isSupportedChain(chainId) ? <WrongNetworkCard /> : renderBody()}
      <PoweredByKodiak />
      <SwitchLocaleLink />
    </S.PageWrapper>
  )
}

export default Liquidity
